import { usePageModels } from '@/context/PageModels';
import AssetDownloadWrapper from '@/features/shared/AssetDownloadWrapper';
import Button from '@/features/shared/Button';
import Chip from '@/features/shared/Chip';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useCardCategories } from '@/features/shared/Hooks/categories/useCardCategories';
import useDownloadResultContent from '@/features/shared/Hooks/useDownloadResultContent';
import Image from '@/features/shared/Image';
import {
  MuiAvatar,
  MuiButtonBase,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiCardHeader,
  MuiCardMedia,
  MuiTypography,
} from '@/features/shared/material-ui';
import PartnerOnly from '@/features/shared/PartnerOnly';
import RichText from '@/features/shared/RichText';
import {
  mapColorNameToContrastColorName,
  mapLinkToProps,
  matchExternalLink,
} from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import { prop } from 'lodash/fp';
import React, { memo, MouseEvent, useCallback, useContext, useMemo } from 'react';
import CardDownloadButton from './CardDownloadButton/CardDownloadButton';
import useStyles from './ContentCard.styles';

const ContentCard = ({
  data,
  cardColor,
  theme,
  className: overrideClassName,
  categoryUrl = {},
  labels: parentLabels = {},
}: {
  data?: CardType & {
    variant?: string;
    isPartnerOnly?: boolean;
    isDownloadable?: boolean;
    cardContentType?: string;
  };
  cardColor?: string;
  theme?: ThemeType;
  className?: string;
  categoryUrl?: any;
  labels?: any;
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { esLabels } = usePageModels();
  const chipColor = useMemo(
    () => mapColorNameToContrastColorName({ colorName: cardColor, darker: true }),
    [cardColor],
  );
  const classes = useStyles({});
  const {
    variant = '',
    cardImage,
    cardLogo,
    cardDescription,
    cardHeading,
    buttonLink,
    cardCategory,
    cardCategoryType,
    cardPublicationDate,
    cardReadTime,
    cardAuthor,
    cardContentType,
    url,
    resultID,
    isPartnerOnly,
    isDownloadable,
  } = data || {};

  const { downloadContent, isDownloading, parsedErrorMessage, error } = useDownloadResultContent(
    url || '',
    contentRef,
    prop('download_button_label', parentLabels),
  );

  const handleDownloadClick = useCallback(() => {
    downloadContent();
  }, [downloadContent]);

  const linkProps = useMemo(
    () => mapLinkToProps({ href: url, openInNewWindow: Boolean(url && matchExternalLink(url)) }),
    [url],
  );

  const page = useContext(BrPageContext);
  const hasCategory = useMemo(() => cardCategory && cardCategory.length > 0, [cardCategory]);

  const {
    queryParams,
    updateQueryParams,
    forceUpdate = () => null,
    labels = {},
  } = useContext(ElasticComponentContext);

  const cardCategoriesProps = useCardCategories(
    page,
    esLabels,
    categoryUrl,
    cardCategory,
    cardContentType,
    cardCategoryType,
  );

  const handleCategoryClick = useCallback(
    ({ e, value, clickType }: { e: MouseEvent; value: string; clickType: string }) => {
      e.preventDefault();
      if (!clickType) {
        return;
      }

      const currentValue = queryParams.filters[clickType] || [];
      if (currentValue.includes(value)) {
        return;
      }

      let parsedValue: string[] = [value];

      if (Array.isArray(currentValue)) {
        parsedValue = [...currentValue, value];
      }
      if (currentValue?.length > 0) {
        parsedValue = [...currentValue, value];
      }

      updateQueryParams('page', 1);
      updateQueryParams(cardCategoryType, parsedValue, true);
      forceUpdate();

      e.stopPropagation();
    },
    [cardCategoryType, updateQueryParams, queryParams, forceUpdate],
  );

  const categories = useMemo(() => {
    const { categoryProps, categoryWrapped, clickType, chipLabel } = cardCategoriesProps;

    return categoryWrapped.map((item: any) => (
      <Chip
        clickable
        label={chipLabel(item)}
        key={item}
        size="small"
        isCategory
        multiple={cardCategory.length > 1}
        theme={theme}
        chipColor={chipColor}
        className={classes.chip}
        onClick={
          !categoryUrl[item || cardContentType]
            ? (e: React.MouseEvent) => handleCategoryClick({ e, value: item, clickType })
            : undefined
        }
        {...categoryProps(item || cardContentType)}
      />
    ));
  }, [
    cardCategoriesProps,
    cardCategory,
    categoryUrl,
    theme,
    chipColor,
    classes.chip,
    handleCategoryClick,
    cardContentType,
  ]);

  const cardClassName = clsx(
    classes.card,
    `${theme}-theme`,
    `bg-color-${cardColor}`,
    overrideClassName,
    'gntc-content-card',
  );

  const hasHeaderInfo = useMemo(() => hasCategory || isPartnerOnly, [hasCategory, isPartnerOnly]);

  return (
    <MuiCard className={cardClassName} elevation={8} ref={contentRef}>
      <AssetDownloadWrapper
        download={isDownloadable}
        isDownloading={isDownloading}
        error={error}
        errorMessage={parsedErrorMessage}
      >
        {cardImage && /image/.test(variant) && (
          <MuiCardMedia className={classes.image}>
            <Image
              image={cardImage}
              type="card"
              size="cover"
              position="center"
              roundedCorners={false}
            />
          </MuiCardMedia>
        )}
        {cardLogo?.original?.url && (
          <MuiCardHeader
            className={classes.cardHeader}
            avatar={<MuiAvatar src={cardLogo?.original.url} />}
          />
        )}
        {(cardDescription || cardCategory || cardHeading) && (
          <MuiCardContent className={classes.content}>
            {hasHeaderInfo && (
              <div className={classes.headerWrapper}>
                {hasCategory && <div className={classes.categories}>{categories}</div>}
                {isPartnerOnly && (
                  <div className="partner-only-wrapper">
                    <PartnerOnly blueLabel theme={theme} />
                  </div>
                )}
              </div>
            )}
            {cardHeading && (
              <MuiTypography
                className={classes.contentHeading}
                variant="h6"
                dangerouslySetInnerHTML={{ __html: cardHeading }}
              />
            )}
            {cardDescription && (
              <RichText theme={theme} className={classes.copy} content={cardDescription} />
            )}
          </MuiCardContent>
        )}
        {buttonLink && (
          <MuiCardActions className={classes.actions}>
            <Button link={buttonLink} theme={theme} />
          </MuiCardActions>
        )}
        <div className={clsx(classes.actions, classes.footerWrapper)}>
          {(cardReadTime || cardPublicationDate || cardAuthor?.name) && (
            <MuiTypography variant="caption" component="div" className={clsx(classes.date)}>
              <div>
                {cardPublicationDate && <span>{cardPublicationDate}</span>}
                {cardPublicationDate && cardReadTime && <span className={classes.divider}>|</span>}
                {cardReadTime && <span>{cardReadTime}</span>}
                {cardAuthor && <p>{cardAuthor?.name}</p>}
              </div>
            </MuiTypography>
          )}
          {isDownloadable && <CardDownloadButton labels={parentLabels} />}
        </div>
        <MuiButtonBase
          {...(isDownloadable ? { onClick: handleDownloadClick } : linkProps)}
          className={classes.link}
          data-resultid={resultID}
          data-datalayer-link-type="card"
          data-datalayer-title={cardHeading}
        />
      </AssetDownloadWrapper>
    </MuiCard>
  );
};

export default memo(ContentCard);
